// components/CareerApplicationForm.tsx
"use client";

import { useState, type FormEvent } from "react";
import { Loader2, CheckCircle2, UploadCloud } from "lucide-react";

const POSITIONS = [
  "Consultant Dermatologist (MD / DNB / DDVL)",
  "Aesthetic Physician (MBBS)",
  "Aesthetic Physician (BDS)",
  "Aesthetic Physician (BAMS)",
  "Aesthetic Physician (BHMS)",
  "Future Opportunities",
];

const MAX_FILE_SIZE_MB = 4;
const ACCEPTED_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

type SubmitState = "idle" | "submitting" | "success" | "error";

export function CareerApplicationForm() {
  const [position, setPosition] = useState("");
  const [fullName, setFullName] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [email, setEmail] = useState("");
  const [resume, setResume] = useState<File | null>(null);
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState<SubmitState>("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [fileError, setFileError] = useState("");

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    setFileError("");
    if (!file) {
      setResume(null);
      return;
    }
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setFileError("Please upload a PDF, DOC, or DOCX file.");
      setResume(null);
      return;
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setFileError(`File must be smaller than ${MAX_FILE_SIZE_MB} MB.`);
      setResume(null);
      return;
    }
    setResume(file);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setErrorMessage("");

    if (!position || !fullName || !contactNumber || !email || !resume || !consent) {
      setErrorMessage("Please fill in all required fields and upload your resume.");
      return;
    }

    setStatus("submitting");

    try {
      const formData = new FormData();
      formData.append("position", position);
      formData.append("fullName", fullName);
      formData.append("contactNumber", contactNumber);
      formData.append("email", email);
      formData.append("resume", resume);

      const res = await fetch("/api/careers-application", {
        method: "POST",
        body: formData,
      });

      if (!res.ok) throw new Error("Submission failed");

      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMessage(
        "Something went wrong while submitting your application. Please try again or email your resume directly to care@skinsioclinics.com."
      );
    }
  }

  if (status === "success") {
    return (
      <div className="flex flex-col items-center rounded-2xl border border-border bg-card p-10 text-center">
        <CheckCircle2 className="mb-4 h-12 w-12 text-primary" />
        <h3 className="text-xl font-bold">Application Submitted</h3>
        <p className="mt-2 text-muted-foreground">
          Thank you for your interest in joining SkinSio Clinics. Our team
          will review your application and get in touch if there's a fit.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-5 rounded-2xl border border-border bg-card p-6 sm:p-8"
    >
      {/* Position */}
      <div>
        <label htmlFor="position" className="mb-1.5 block text-sm font-semibold">
          Position Applying For <span className="text-primary">*</span>
        </label>
        <select
          id="position"
          required
          value={position}
          onChange={(e) => setPosition(e.target.value)}
          className="w-full rounded-lg border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="" disabled>
            Select a position
          </option>
          {POSITIONS.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </div>

      {/* Full Name */}
      <div>
        <label htmlFor="fullName" className="mb-1.5 block text-sm font-semibold">
          Full Name <span className="text-primary">*</span>
        </label>
        <input
          id="fullName"
          type="text"
          required
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          placeholder="Dr. Jane Doe"
          className="w-full rounded-lg border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {/* Contact Number */}
      <div>
        <label htmlFor="contactNumber" className="mb-1.5 block text-sm font-semibold">
          Contact Number <span className="text-primary">*</span>
        </label>
        <input
          id="contactNumber"
          type="tel"
          required
          value={contactNumber}
          onChange={(e) => setContactNumber(e.target.value)}
          placeholder="+91 XXXXX XXXXX"
          className="w-full rounded-lg border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {/* Email */}
      <div>
        <label htmlFor="email" className="mb-1.5 block text-sm font-semibold">
          Email Address <span className="text-primary">*</span>
        </label>
        <input
          id="email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className="w-full rounded-lg border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {/* Resume Upload */}
      <div>
        <label htmlFor="resume" className="mb-1.5 block text-sm font-semibold">
          Upload Resume / CV <span className="text-primary">*</span>
        </label>
        <label
          htmlFor="resume"
          className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border bg-background px-4 py-6 text-center transition hover:border-primary"
        >
          <UploadCloud className="h-6 w-6 text-muted-foreground" />
          <span className="text-sm font-medium">
            {resume ? resume.name : "Click to upload your resume"}
          </span>
          <span className="text-xs text-muted-foreground">
            Accepted formats: PDF, DOC, DOCX (Maximum 4 MB)
          </span>
          <input
            id="resume"
            type="file"
            required
            accept=".pdf,.doc,.docx"
            onChange={handleFileChange}
            className="hidden"
          />
        </label>
        {fileError && (
          <p className="mt-1.5 text-xs font-medium text-destructive">{fileError}</p>
        )}
      </div>

      {/* Consent */}
      <label className="flex items-start gap-2.5 text-sm text-muted-foreground">
        <input
          type="checkbox"
          required
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-border text-primary focus:ring-primary"
        />
        I confirm that the information provided is accurate and I consent
        to SkinSio Clinics reviewing my application.
      </label>

      {errorMessage && (
        <p className="text-sm font-medium text-destructive">{errorMessage}</p>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-lg transition hover:bg-primary/90 disabled:opacity-60"
      >
        {status === "submitting" && <Loader2 className="h-4 w-4 animate-spin" />}
        {status === "submitting" ? "Submitting..." : "Submit Application"}
      </button>
    </form>
  );
}
